import { motion } from "framer-motion";
import { useEffect } from "react";
import { BellRing } from "lucide-react";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import type { PushPrimer as PushPrimerState } from "../lib/usePushPrimerTypes";

interface PushPrimerProps {
  primer: PushPrimerState;
}

/**
 * The soft ask, shown before the system one.
 *
 * It exists because the OS prompt is one-shot on iOS: raise it cold on the
 * dashboard and a reflex "Don't Allow" ends push for that install permanently.
 * This screen's whole job is to make the tap that follows it a considered one.
 *
 * So the copy names what is at stake rather than offering a favour. "Want a
 * nudge?" invites "no thanks" — a nudge is something you can decline without
 * losing anything. What is actually on the other side of that tap is the
 * restaurant address, the table's messages and the day-of reminders, none of
 * which reach a booker any other way, so the screen says that and says what
 * happens without it: you keep checking back yourself.
 *
 * "Not now" is a real option and is styled like one. A primer that only offers
 * the happy path trains people to dismiss it with the system gesture, which
 * reads to us as no answer at all and puts us straight back here tomorrow.
 */
export default function PushPrimer({ primer }: PushPrimerProps) {
  const { t } = useT();

  useEffect(() => {
    if (primer.show) tracking.pushPrimerShown();
  }, [primer.show]);

  if (!primer.show) return null;

  return (
    <div className="modal modal-open z-[9999]">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="modal-box card max-w-md"
        role="dialog"
        aria-modal="true"
      >
        {/* No backdrop click-through and no close cross: the two buttons are
            the only ways out. A dismissal we cannot tell apart from a stray
            tap is the one answer this screen must not collect, because it is
            indistinguishable from "ask me again". */}
        <div className="w-14 h-14 rounded-full flex items-center justify-center
          bg-primary-100 text-primary-600
          dark:bg-primary-500/15 dark:text-primary-400
          dark:shadow-[inset_0_0_0_1px_rgba(239,98,37,0.35)]">
          <BellRing className="w-7 h-7" strokeWidth={2} />
        </div>

        <h3 className="font-display font-semibold text-[24px] leading-[1.16] tracking-tight
          text-neutral-900 dark:text-neutral-50 mt-5">
          {t("push.primer.title")}
        </h3>
        <p className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-2.5">
          {t("push.primer.body")}
        </p>

        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-7">
          <button
            onClick={() => {
              tracking.pushPrimerDismissed();
              primer.dismiss();
            }}
            className="btn btn-secondary flex-1 py-3.5"
          >
            {t("push.primer.notNow")}
          </button>
          <button
            onClick={() => {
              tracking.pushPrimerAllowed();
              void primer.allow();
            }}
            className="btn btn-primary flex-1 py-3.5"
          >
            {t("push.primer.allow")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
